import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bot, X, Send, Sparkles, ArrowRight, Loader2, ExternalLink } from "lucide-react";

const SUGGESTIONS = [
  "What should I do next?",
  "Create a Quote",
  "Show delayed Work Orders",
  "Open Product Master",
  "How do I create a Compound Batch?",
  "Show pending Material Receipts",
  "Which machines are under maintenance?",
  "Update Raw Material prices",
];

const sessionId = (() => {
  let s = localStorage.getItem("copilot_session");
  if (!s) { s = Math.random().toString(36).slice(2); localStorage.setItem("copilot_session", s); }
  return s;
})();

function highlightEl(testid) {
  if (!testid) return;
  setTimeout(() => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("copilot-pulse");
    setTimeout(() => el.classList.remove("copilot-pulse"), 3200);
  }, 600);
}

export default function CopilotWidget() {
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]); // {role, reply, steps, actions, items}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", reply: q }]);
    setBusy(true);
    try {
      const r = await api.post("/copilot/chat", { message: q, session_id: sessionId, current_route: loc.pathname });
      setMsgs((m) => [...m, { role: "bot", ...r.data }]);
    } catch {
      setMsgs((m) => [...m, { role: "bot", reply: "Sorry, I couldn't reach the assistant. Please try again." }]);
    } finally { setBusy(false); }
  };

  const runAction = (a) => {
    if (!a?.route) return;
    nav(a.route);
    if (a.highlight) highlightEl(a.highlight);
    setOpen(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-fab"
          className="fixed bottom-20 right-6 z-[60] w-14 h-14 rounded-full bg-[#ab0000] hover:bg-[#8a0000] text-white shadow-xl shadow-[#ab0000]/30 grid place-items-center transition-transform hover:scale-105"
          aria-label="Open Sahara Copilot">
          <Bot size={24} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white" />
        </button>
      )}

      {open && (
        <div data-testid="copilot-panel" className="fixed bottom-20 right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[600px] max-h-[78vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-fade-up">
          <div className="bg-[#ab0000] text-white px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/15 grid place-items-center"><Bot size={20} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold leading-tight">Sahara Copilot</div>
              <div className="text-[11px] text-white/80">Ask me anything or tell me what you want to do.</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="copilot-close" className="p-1 rounded hover:bg-white/15"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50/60">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <div className="bg-white border border-slate-200 rounded-xl p-3 text-sm text-slate-700 flex gap-2">
                  <Sparkles size={16} className="text-[#ab0000] shrink-0 mt-0.5" />
                  <span>Hi! I'm your ERP copilot. I can guide you, pull live data, and take you to the right screen. Try one of these:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid={`copilot-suggestion`} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#ab0000] hover:text-[#ab0000] transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}

            {msgs.map((m, i) => m.role === "user" ? (
              <div key={i} className="flex justify-end"><div className="bg-[#ab0000] text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm max-w-[85%]" data-testid="copilot-user-msg">{m.reply}</div></div>
            ) : (
              <div key={i} className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2.5 text-sm max-w-[90%] space-y-2 w-full" data-testid="copilot-bot-msg">
                <div className="text-slate-800 whitespace-pre-line">{m.reply}</div>
                {m.steps?.length > 0 && (
                  <ol className="space-y-1.5 mt-1">
                    {m.steps.map((s, j) => (
                      <li key={j} className="flex gap-2 text-slate-700"><span className="shrink-0 w-5 h-5 rounded-full bg-[#fce8e8] text-[#ab0000] text-[11px] font-bold grid place-items-center">{j + 1}</span><span>{s}</span></li>
                    ))}
                  </ol>
                )}
                {m.items?.length > 0 && (
                  <div className="mt-1 border-t border-slate-100 pt-2 space-y-1">
                    {m.items.map((it, j) => (
                      <button key={j} onClick={() => runAction(it)} data-testid="copilot-item" className="w-full flex items-center justify-between text-left text-xs text-slate-600 hover:text-[#ab0000] py-1">
                        <span className="truncate">{it.label}</span><ExternalLink size={12} className="shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
                {m.actions?.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {m.actions.map((a, j) => (
                      <button key={j} onClick={() => runAction(a)} data-testid="copilot-action" className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#ab0000] text-white px-3 py-1.5 rounded-lg hover:bg-[#8a0000]">
                        {a.label || "Open"} <ArrowRight size={13} />
                      </button>
                    ))}
                  </div>
                )}
              </div></div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 text-sm text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Thinking…</div></div>}
            <div ref={endRef} />
          </div>

          <div className="border-t border-slate-200 p-2.5 flex items-center gap-2 bg-white">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question or type a command…" data-testid="copilot-input"
              className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#ab0000]/20" />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="copilot-send" className="h-10 w-10 rounded-lg bg-[#ab0000] hover:bg-[#8a0000] text-white grid place-items-center disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
